<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper.js';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactAPI from 'dashboard/api/contacts';

const { t } = useI18n();
const route = useRoute();

const auditItems = ref([]);
const isFetching = ref(false);

const statusLabel = status => {
  const map = { open: 'Open', resolved: 'Resolved', pending: 'Pending', snoozed: 'Snoozed' };
  return map[status] || status;
};

const fetchAudit = async () => {
  const contactId = route.params.contactId;
  if (!contactId) return;

  isFetching.value = true;
  try {
    const { data } = await ContactAPI.getRequestsAudit(contactId);
    auditItems.value = data.payload || [];
  } catch {
    auditItems.value = [];
  } finally {
    isFetching.value = false;
  }
};

const openConversation = displayId => {
  const path = frontendURL(
    `accounts/${route.params.accountId}/conversations/${displayId}`
  );
  window.open(window.chatwootConfig.hostURL + path, '_blank', 'noopener noreferrer');
};

onMounted(fetchAudit);
watch(() => route.params.contactId, fetchAudit);
</script>

<template>
  <div
    v-if="isFetching"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div
    v-else-if="auditItems.length > 0"
    class="px-6 py-4 space-y-4"
  >
    <p class="text-sm font-medium text-n-slate-12">
      {{ t('CONTACTS_LAYOUT.SIDEBAR.REQUESTS_AUDIT.DESCRIPTION') }}
    </p>
    <div class="space-y-3">
      <div
        v-for="item in auditItems"
        :key="item.conversation_id"
        class="p-3 rounded-lg border border-n-slate-6 bg-n-surface-2 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 cursor-pointer transition-colors"
        @click="openConversation(item.display_id)"
      >
        <div class="flex items-center justify-between gap-2 mb-1">
          <span class="text-xs font-medium text-n-slate-11">
            #{{ item.display_id }}
            <span
              v-if="item.inbox_name"
              class="text-n-slate-10"
            >
              · {{ item.inbox_name }}
            </span>
          </span>
          <span class="text-xs text-n-slate-10">
            {{ shortTimestamp(dynamicTime(item.created_at)) }}
          </span>
        </div>
        <p class="text-sm text-n-slate-12 line-clamp-2 mb-2">
          {{ item.first_request_content || t('CONTACTS_LAYOUT.SIDEBAR.REQUESTS_AUDIT.NO_CONTENT') }}
        </p>
        <div class="flex flex-wrap gap-1">
          <span
            class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-n-slate-4 text-n-slate-11"
          >
            {{ statusLabel(item.status) }}
          </span>
          <span
            v-for="label in (item.labels || [])"
            :key="label"
            class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-n-brand/10 text-n-brand"
          >
            {{ label }}
          </span>
        </div>
      </div>
    </div>
  </div>
  <p
    v-else
    class="px-6 py-10 text-sm leading-6 text-center text-n-slate-11"
  >
    {{ t('CONTACTS_LAYOUT.SIDEBAR.REQUESTS_AUDIT.EMPTY_STATE') }}
  </p>
</template>
